<template>

    <div class="documentation-main w-full">
        <h1 class="p-2">{{ info.title }}</h1>

        <vs-row>
            <vs-col vs-w="6">
                <div class="flex items-center p-2">
                    <div class="mr-4">API Base URL:</div>
                    <vs-chip color="primary">{{apiUrl}}</vs-chip>
                    <copy :text="apiUrl"></copy>
                </div>

                <div class="p-2">
                    <vs-alert class="w-full">
                        <div class="w-full flex items-center justify-between">
                            <div class="flex items-center">
                                <div v-html="methodColor(path.method)"></div>
                                <div class="ml-4">{{ path.path }}</div>
                                <copy :text="path.path"></copy>
                            </div>
                            <vs-chip v-if="path.security" color="danger" transparent class="flex items-center m-0">
                                <vs-icon v-bind="$iconData('icon-lock', 'fi-rr-lock')"></vs-icon>
                                <div class="ml-2">{{ path.security }}</div>
                            </vs-chip>
                        </div>
                    </vs-alert>
                    <div class="mt-4">{{ info.description }}</div>
                </div>

                <div class="p-2" v-if="Object.keys(request).length">
                    <h3>Request</h3>

                    <div v-for="(requestValue, requestName) of request">
                        <div class="p-2">{{ ucFirst(requestName) }}</div>
                        <div v-for="(value, name) of requestValue" :key="name" class="headers-item">
                            <param-element
                                :description="value.description"
                                :name="name"
                                :type="value.type"
                                :required="value.required"
                                :format="value.format"
                            ></param-element>
                        </div>
                    </div>
                </div>

                <div class="p-2" v-if="Object.keys(response).length">
                    <h3>Response</h3>
                    <div>
                        <ul class="flex items-center">
                            <li
                                class="p-2 cursor-pointer"
                                @click="selectStatus(index)"
                                v-for="(status, index) of Object.keys(response)"
                                :key="index"
                                :class="{'activeTab': selectedTab === index}"
                            >
                                <b>{{status}}</b>
                            </li>
                        </ul>
                        <div class="mt-4 mb-4">{{ findStatusCode(Object.keys(response)[selectedTab]) }}</div>
                        <div v-for="(cTypeValue, type) in responseStatusCodeParams" :key="type" class="mb-4">
                            <div v-for="(data, index) of cTypeValue" :key="index">
                                <div v-for="(value, name) of data" :key="name" class="headers-item">
                                    <param-element
                                        :description="value.description"
                                        :name="name"
                                        :type="value.type"
                                        :required="value.required"
                                        :children="value.children"
                                    ></param-element>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </vs-col>

            <vs-col vs-w="6">
                <div class="p-2" v-if="Object.keys(request).length">
                    <div v-for="(exampleValue, header) in exampleCodeObj" :key="header" v-if="request">
                        <div class="p-4" style="background-color: #000; color: white">{{ucFirst(header)}}</div>
                        <highlight-code lang="json">{{exampleValue}}</highlight-code>
                    </div>
                </div>
                <div class="p-2" v-if="Object.keys(request).length">
                    <div class="p-4 flex justify-between items-center" style="background-color: #000; color: white">
                        <vs-select autocomplete v-model="selectedExampleItem">
                            <div :key="index" v-for="(item, index) in codeMenu">
                                <vs-select-group :title="item.title" v-if="item.group">
                                    <vs-select-item :key="index" :value="item.value" :text="item.text" v-for="(item,index) in item.group"/>
                                </vs-select-group>
                            </div>
                        </vs-select>
                        <copy color="#fff" :text="exampleCode"></copy>
                    </div>
                    <highlight-code :lang="selectLang">{{exampleCode}}</highlight-code>
                </div>
                <div class="p-2" v-if="Object.keys(response).length">
                    <div class="p-4 flex justify-between items-center" style="background-color: #000; color: white">
                        <vs-select autocomplete v-model="statusCode.selectListValue" v-if="statusCode.list.length">
                            <vs-select-item v-for="(item, index) of statusCode.list" :key="index" :value="item.value" :text="item.text"/>
                        </vs-select>
                        <div v-else>Example</div>
                    </div>
                    <highlight-code lang="json">{{statusCode.selectCode}}</highlight-code>
                </div>
            </vs-col>
        </vs-row>
    </div>
</template>

<script>
import ParamElement from '../components/ParamElement'
import Copy from '../components/Copy'
import {mixins} from '../mixins'
import ErrorHandler from "@/utils/errorHandler"
import status from 'statuses'
import CodeBlock from "../components/CodeBlock"

export default {
    mixins: [mixins],
    props: {
        jsonData: {
            type: Object
        }
    },

    components: {
        'param-element': ParamElement,
        'code-block': CodeBlock,
        'copy': Copy
    },

    watch: {
        jsonData() {
            this.parseDataJson()
        },

        async selectedExampleItem(value) {
            await this.selectExampleCode(value)
        },

        'statusCode.selectListValue'(index) {
            this.selectExElement(index)
        }
    },

    data() {
        return {
            apiUrl: process.env.VUE_APP_API_URL_DOCS,
            selectedExampleItem: 'curl',
            codeMenu: [
                {
                    title: 'JavaScript',
                    lang: 'js',
                    group: [
                        {text: 'Axios', value: 'axios'},
                        {text: 'Fetch', value: 'fetch'},
                        {text: 'XMLHttpRequest', value: 'XMLHttpRequest'},
                        {text: 'jQuery', value: 'jq'},
                    ]
                },
                {
                    title: 'PHP',
                    lang: 'php',
                    group: [
                        {text: 'cURL', value: 'curl'}
                    ]
                },
                {
                    title: 'Node',
                    lang: 'js',
                    group: [
                        {text: 'Native', value: 'native_node'},
                        {text: 'Request', value: 'request_node'},
                        {text: 'Unirest', value: 'unirest_node'},
                        {text: 'Fetch', value: 'fetch_node'},
                        {text: 'Axios', value: 'nodeAxios'},
                    ]
                },
                {
                    title: 'Go',
                    lang: 'go',
                    group: [
                        {text: 'Go', value: 'go'}
                    ]
                },
            ],

            security: ['X-Secret-Key'],
            tabMounted: false,
            selectedTab: 0,
            responseStatusCodeParams: {},

            info: {},
            path: {},
            request: {},
            response: {},

            exampleCodeObj: {},
            exampleCode: {},
            selectLang: '',

            statusCode: {
                selectListValue: 0,
                list: [],
                selectCode: {}
            }
        }
    },

    methods: {
        selectStatus(index) {
            if (index === undefined) {
                index = this.selectedTab
            }
            this.selectedTab = index
            const tab = Object.keys(this.response)[index]
            if (this.response[tab]) {
                this.responseStatusCodeParams = this.response[tab].content
            }

            this.responseExCode()
        },

        async selectExampleCode() {
            try {
                const url = `${this.$route.params.api}`
                const params = {
                    method: this.path.method,
                    url: `${this.apiUrl}${this.path.path}`,
                    headers: this.exampleCodeObj.headers,
                    data: this.exampleCodeObj.body
                }

                if (params.headers.hasOwnProperty('Content-Type')) {
                    delete params.headers['Content-Type']
                }

                const {title, lang} = this.codeMenu.find(group => group.group.find(item => item.value === this.selectedExampleItem))
                this.selectLang = lang
                const res = await import(`@/views/_documentation/codes/${title.toLowerCase()}/${this.selectedExampleItem}.js`)
                this.exampleCode = res.default(params)

            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        findStatusCode(code) {
            return status(code)
        },

        ucFirst(str) {
            if (!str) return str
            return str[0].toUpperCase() + str.slice(1)
        },

        createExampleRequest() {
            try {
                const data = {}
                Object.keys(this.request).map(header => {
                    data[header] = {}
                    Object.keys(this.request[header]).map(key => {
                        let ex = this.request[header][key].example ? this.request[header][key].example : 123
                        if (this.security.includes(key)) {
                            ex = this.request[header][key].example = this.$route.params.hash
                        }
                        Object.assign(data[header], {[key]: ex})
                    })
                })
                this.exampleCodeObj = data
            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        parseDataJson() {
            try {
                this.selectedExampleItem = 'curl'
                this.selectedTab = 0
                this.statusCode.selectListValue = 0
                this.statusCode.list = []

                Object.keys(this.jsonData).map(path => {
                    Object.keys(this.jsonData[path]).map(method => {
                        const a = this.jsonData[path][method]

                        this.info = a.info
                        this.path.path = path
                        this.path.method = method
                        this.path.security = a.security

                        if (a.request) {
                            this.request = a.request
                        } else {
                            this.request = {}
                        }

                        if (a.response) {
                            this.response = a.response
                        } else {
                            this.response = {}
                        }
                    })
                })

                this.createExampleRequest()
                this.selectStatus()
                this.selectExampleCode()
            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        responseExCode(data) {
            this.statusCode.selectListValue = 0
            this.statusCode.list = []

            try {
                let a
                const nameList = []

                if (!data) {
                    const tab = Object.keys(this.response)[this.selectedTab]
                    a = this.response[tab].responseExamples
                } else {
                    a = data
                }

                a.forEach((item, index) => item.title && nameList.push({text: item.title, value: index}))
                this.statusCode.list = nameList
                this.statusCode.selectCode = a[this.statusCode.selectListValue].example
            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        selectExElement(index) {
            const tab = Object.keys(this.response)[this.selectedTab]
            this.statusCode.selectCode = this.response[tab].responseExamples[index].example
        }
    },

    mounted() {
        try {
            this.parseDataJson()

            this.tabMounted = true
        } catch (e) {
            new ErrorHandler(e).send
        }
    }
}
</script>

<style lang="scss">
.vs-select-group {
    h4 {
        font-size: 14px;
        color: gray;
    }
}

.documentation-main {
    padding-left: 250px;

    .activeTab {
        color: rgba(var(--vs-primary), 1);
        position: relative;
        &:after {
            content: '';
            height: 2px;
            width: 100%;
            position: absolute;
            bottom: 0;
            left: 0;
            background-color: rgba(var(--vs-primary), 1);
        }
    }

    .input-select-con {
        input {
            background-color: #636363;
            border: none;
        }
    }

    .headers {
        &-item {
            margin-bottom: 1rem;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    .vs-tabs--ul {
        box-shadow: none !important;
    }

    code {
        border-radius: 0 !important;
    }
}
</style>
